import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Star, ThumbsUp, ThumbsDown, TrendingUp, Users,
  MessageSquare, ChevronDown, ChevronUp, FileDown, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const NIVEAUX = [
  { value: 'tres_insatisfait', label: 'Très insatisfait', color: 'bg-red-500', score: 1 },
  { value: 'insatisfait', label: 'Insatisfait', color: 'bg-orange-500', score: 2 },
  { value: 'moyennement_satisfait', label: 'Moyennement', color: 'bg-amber-500', score: 3 },
  { value: 'satisfait', label: 'Satisfait', color: 'bg-emerald-500', score: 4 },
  { value: 'tres_satisfait', label: 'Très satisfait', color: 'bg-green-600', score: 5 }
];

const SatisfactionManager = () => {
  const [evaluations, setEvaluations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [evalsRes, statsRes] = await Promise.all([
        api.get('/evaluations-satisfaction'),
        api.get('/evaluations-satisfaction/stats')
      ]);
      setEvaluations(evalsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const now = new Date();
      const response = await api.get(`/stats/satisfaction/pdf?mois=${now.getMonth() + 1}&annee=${now.getFullYear()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Synthese_Satisfaction_${now.getMonth() + 1}_${now.getFullYear()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Synthèse téléchargée avec succès');
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getNiveauInfo = (value) => NIVEAUX.find(n => n.value === value);

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec titre et bouton télécharger */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
          Évaluations de Satisfaction
        </h2>
        <Button
          onClick={handleDownloadPdf}
          disabled={downloadingPdf}
          variant="outline"
          className="rounded-xl"
          data-testid="download-satisfaction-pdf-btn"
        >
          {downloadingPdf ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Génération...
            </>
          ) : (
            <>
              <FileDown className="w-4 h-4 mr-2" />
              Télécharger PDF
            </>
          )}
        </Button>
      </div>

      {/* Stats globales */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Réponses</p>
                <p className="text-3xl font-bold text-slate-900">{stats?.total || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-purple-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-3xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Moyenne</p>
                <div className="flex items-center gap-2">
                  <p className="text-3xl font-bold text-amber-700">{stats?.moyenne_globale || 0}</p>
                  <span className="text-lg text-slate-500">/5</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
                <Star className="w-6 h-6 text-amber-600 fill-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Recommandent</p>
                <p className="text-3xl font-bold text-emerald-700">{stats?.recommandations?.oui || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <ThumbsUp className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-rose-50 rounded-3xl border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Ne recommandent pas</p>
                <p className="text-3xl font-bold text-red-700">{stats?.recommandations?.non || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center">
                <ThumbsDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Moyennes par critère */}
      {stats?.par_critere && Object.keys(stats.par_critere).length > 0 && (
        <Card className="bg-white rounded-3xl shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Moyenne par Critère
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(stats.par_critere).map(([critere, moyenne]) => (
              <div key={critere} className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">{critere}</span>
                    <span className="text-sm text-slate-500">{moyenne}/5</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5">
                    <div 
                      className={`h-2.5 rounded-full ${
                        moyenne >= 4 ? 'bg-emerald-500' :
                        moyenne >= 3 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${(moyenne / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Liste des évaluations */}
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-purple-600" />
            Détail des Évaluations ({evaluations.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {evaluations.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Star className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucune évaluation de satisfaction</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {evaluations.map((eval_) => (
                <div key={eval_.id} className="p-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpandedId(expandedId === eval_.id ? null : eval_.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-600 flex items-center justify-center text-white font-semibold">
                        {eval_.apprenant_prenom?.[0]}{eval_.apprenant_nom?.[0]}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {eval_.apprenant_prenom} {eval_.apprenant_nom}
                        </p>
                        <p className="text-sm text-slate-500">
                          {new Date(eval_.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {eval_.recommanderait === true && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <ThumbsUp className="w-3 h-3 mr-1" />
                          Recommande
                        </Badge>
                      )}
                      {eval_.recommanderait === false && (
                        <Badge className="bg-red-100 text-red-700">
                          <ThumbsDown className="w-3 h-3 mr-1" />
                          Ne recommande pas
                        </Badge>
                      )}
                      {expandedId === eval_.id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                  
                  {expandedId === eval_.id && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                      {/* Évaluations par critère */}
                      <div className="grid grid-cols-2 gap-3">
                        {Object.entries(eval_.evaluations || {}).map(([critere, niveau]) => {
                          const niveauInfo = getNiveauInfo(niveau);
                          return (
                            <div key={critere} className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                              <span className="text-sm text-slate-700">{critere}</span>
                              <Badge className={`${niveauInfo?.color || 'bg-slate-200'} text-white`}>
                                {niveauInfo?.label || niveau}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                      
                      {eval_.commentaire_global && (
                        <div className="bg-slate-50 rounded-xl p-4">
                          <p className="text-sm font-medium text-slate-700 mb-1">Commentaire</p>
                          <p className="text-slate-600">{eval_.commentaire_global}</p>
                        </div>
                      )}
                      
                      {eval_.suggestions && (
                        <div className="bg-amber-50 rounded-xl p-4">
                          <p className="text-sm font-medium text-amber-700 mb-1">Suggestions</p>
                          <p className="text-amber-600">{eval_.suggestions}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SatisfactionManager;
